import { useState, useEffect, useCallback, useMemo } from 'react';

interface VirtualScrollingOptions {
  itemHeight: number;
  containerHeight: number;
  overscan?: number;
}

interface VirtualScrollingResult<T> {
  visibleItems: T[];
  totalHeight: number;
  offsetY: number;
  startIndex: number;
  endIndex: number;
}

/**
 * Custom hook for implementing virtual scrolling to optimize performance with large lists.
 * Only renders visible items plus a small overscan buffer.
 * 
 * @param items - Array of items to virtualize
 * @param options - Configuration options for virtual scrolling
 * @returns Object containing visible items and positioning information
 */

export const useVirtualScrolling = <T>(
  items: T[],
  options: VirtualScrollingOptions
): VirtualScrollingResult<T> => {
  const { itemHeight, containerHeight, overscan = 5 } = options;
  const [scrollTop, setScrollTop] = useState(0);

  const handleScroll = useCallback((event: Event) => {
    if (typeof window !== 'undefined') {
      const target = event.target as HTMLElement;
      setScrollTop(target.scrollTop);
    }
  }, []);

  const { visibleItems, totalHeight, offsetY, startIndex, endIndex } = useMemo(() => {
    const itemCount = items.length;
    const totalHeight = itemCount * itemHeight;
    
    // Calculate visible range
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const endIndex = Math.min(
      itemCount - 1,
      Math.ceil((scrollTop + containerHeight) / itemHeight) + overscan
    );
    
    const visibleItems = items.slice(startIndex, endIndex + 1);
    const offsetY = startIndex * itemHeight;

    return {
      visibleItems,
      totalHeight,
      offsetY,
      startIndex,
      endIndex
    };
  }, [items, itemHeight, containerHeight, scrollTop, overscan]);

  return {
    visibleItems,
    totalHeight,
    offsetY,
    startIndex,
    endIndex
  };
};

/**
 * Custom hook for implementing virtual scrolling with grid layout.
 * Optimizes performance by only rendering visible rows of items.
 * 
 * @param items - Array of items to virtualize in grid format
 * @param options - Configuration options including grid-specific settings
 * @returns Object containing visible rows and positioning information
 */
export const useVirtualScrollingGrid = <T>(
  items: T[],
  options: VirtualScrollingOptions & { 
    itemsPerRow: number;
    itemWidth: number;
    gap?: number;
  }
): VirtualScrollingResult<T[]> => {
  const { itemHeight, containerHeight, itemsPerRow, overscan = 2 } = options;
  const [scrollTop, setScrollTop] = useState(0);

  const { visibleRows, totalHeight, offsetY, startRowIndex, endRowIndex } = useMemo(() => {
    // Group items into rows
    const rows: T[][] = [];
    for (let i = 0; i < items.length; i += itemsPerRow) {
      rows.push(items.slice(i, i + itemsPerRow));
    }

    const rowCount = rows.length;
    const totalHeight = rowCount * itemHeight;
    
    // Calculate visible row range
    const startRowIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const endRowIndex = Math.min(
      rowCount - 1,
      Math.ceil((scrollTop + containerHeight) / itemHeight) + overscan
    );
    
    const visibleRows = rows.slice(startRowIndex, endRowIndex + 1);
    const offsetY = startRowIndex * itemHeight;

    return {
      visibleRows,
      totalHeight,
      offsetY,
      startRowIndex,
      endRowIndex
    };
  }, [items, itemHeight, containerHeight, itemsPerRow, scrollTop, overscan]);

  return {
    visibleItems: visibleRows,
    totalHeight,
    offsetY,
    startIndex: startRowIndex,
    endIndex: endRowIndex
  };
};